import { useEffect, useState } from 'react';
import axios from 'config/axios';
import type { CollegesGrid } from 'global/colleges';
import { DEFAULT_CYCLE_YEAR } from 'global/fillapp';

const CACHE_KEY_PREFIX = 'fillapp.colleges.v3.';

// Same stale-while-revalidate pattern as useFillAppSchema: render the cached
// grid immediately, always refetch so a stale entry self-heals.
export function useFillAppColleges(cycleYear: number = DEFAULT_CYCLE_YEAR) {
    const [grid, setGrid] = useState<CollegesGrid | null>(() => {
        try {
            const cached = localStorage.getItem(CACHE_KEY_PREFIX + cycleYear);
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(grid === null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        axios.get(`fillapp/colleges/${cycleYear}/`)
            .then(res => {
                if (cancelled) return;
                const fresh = JSON.stringify(res.data);
                try {
                    if (localStorage.getItem(CACHE_KEY_PREFIX + cycleYear) !== fresh) {
                        localStorage.setItem(CACHE_KEY_PREFIX + cycleYear, fresh);
                    }
                } catch {
                    // localStorage full — skip caching
                }
                setGrid(res.data);
                setLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                setError(err?.response?.data?.message || 'Failed to load colleges.');
                setLoading(false);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cycleYear]);

    return { grid, loading, error };
}
